import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { IntlManager, NotificationManager, SiteConfigManager } from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import { Button, DefaultPopup, Icon, Label } from '@atlas/ui';

import { ChatNameLiveChat } from 'components/LiveChat/LiveChatFactory';
import useLiveChat from 'components/LiveChat/useLiveChat';

const { CLIENT_PROFILE } = COMPONENT_TYPES;
const { payment } = clientProfileRoutes;

const SuccessfulWithdrawalPopup = ({ data, id }) => {
  const { amount, paymentId = '', paymentMethod, publicPaymentId } = data;
  const requestId = publicPaymentId ?? paymentId;
  const { transactionHistory } = paymentRoutes;
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
  const redirectButtonLink = SiteConfigManager.getConfig(
    'config.config.payments.successPopup.buttonLink',
  );
  const redirectButtonLabel = SiteConfigManager.getConfig(
    'config.config.payments.successPopup.buttonLabel',
  );
  const isChatAvailable = SiteConfigManager.getConfig(
    'config.config.support.live_chat.mobile.is_available',
  );
  const chatType = SiteConfigManager.getConfig('config.config.support.live_chat.mobile.type');
  const now = dayjs();
  const formattedDate = now.format('DD MMM YYYY HH:mm:ss');
  const { toggleIsOpened: toggleIsChatOpened } = useLiveChat();

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);
  const handleClickChat = useCallback(() => {
    toggleIsChatOpened();
    handleClose();
  }, [handleClose, toggleIsChatOpened]);

  return (
    <DefaultPopup
      className="successful-withdrawal-popup"
      headerTitle="Withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="popup__content">
        <div className="popup__content__text-wrapper popup__content__header">
          <Icon className="popup__content__success-icon" height={49} name="success" width={49} />
          <Label className="popup__content__main-text" message="withdraw_submitted_title" />
          <Label className="popup__content__subtext" message="withdraw_submitted_text" />
          {isChatAvailable && chatType === ChatNameLiveChat && (
            <div className="popup__content__chat-wrapper">
              <Label className="popup__content__subtext" message="withdraw_need_help" />
              <Button
                className="popup__content__chat"
                label="withdraw_contact_live_chat"
                onClick={handleClickChat}
                type="text"
              />
            </div>
          )}
        </div>

        <div className="popup__content__separator" />

        <div className="popup__content__inner">
          <div className="popup__content__text-wrapper popup__content__inner-wrapper">
            <Label
              className="popup__content__subtext"
              message="withdraw_submitted_payment_method"
            />
            <div className="popup__content__main-text">{paymentMethod?.name}</div>
          </div>
          <div className="popup__content__text-wrapper popup__content__inner-wrapper">
            <Label className="popup__content__subtext" message="withdraw_submitted_amount" />
            <div className="popup__content__main-text">
              {IntlManager.formatMoney(parseInt(amount))}
            </div>
          </div>
          <div className="popup__content__text-wrapper popup__content__inner-wrapper">
            <Label className="popup__content__subtext" message="withdraw_submitted_requested_at" />
            <div className="popup__content__main-text">{formattedDate}</div>
          </div>
          <div className="popup__content__text-wrapper popup__content__inner-wrapper">
            <Label className="popup__content__subtext" message="withdraw_submitted_requested_id" />
            <div className="popup__content__main-text">{requestId}</div>
          </div>
        </div>

        <div className="popup__content__separator" />
      </div>
      <div className="popup__footer">
        <Link className="popup__link" to={`${clientProfilePath}/${payment}/${transactionHistory}`}>
          <Button
            className="popup__button"
            label="withdraw_submitted_go_to_transaction_history"
            onClick={handleClose}
            size="big"
            type="primary"
          />
        </Link>
        {redirectButtonLink && (
          <Link className="popup__link" to={redirectButtonLink}>
            <Button className="popup__button" onClick={handleClose} size="big" type="secondary">
              {redirectButtonLabel}
            </Button>
          </Link>
        )}
      </div>
    </DefaultPopup>
  );
};

SuccessfulWithdrawalPopup.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.number,
    currency: PropTypes.string,
    paymentId: PropTypes.string,
    paymentMethod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    publicPaymentId: PropTypes.string,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SuccessfulWithdrawalPopup;
